import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Modal, Form, Input, Select, DatePicker, message, Row, Col } from "antd";
import DeliveryServ from "../service/DeliveriesServ";
import OrdersServ from "../service/OrdersServ";
import DeliveryStaffServ from "../service/DeliveryStaffServ";
import GlobalButton from "../util/GlobalButton";

const { Option } = Select;

const AddDeliveries = ({ open, deliverieDetails, isEditing, refreshDeliveries }) => {
    const [form] = Form.useForm();
    const [orders, setOrders] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [ordersRes, staffRes] = await Promise.all([
                OrdersServ.getSellerAllOrders(),
                DeliveryStaffServ.index()
            ]);
            setOrders(ordersRes?.data || ordersRes || []);
            setStaff(staffRes || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchData();
    }, [open]);

    useEffect(() => {
        if (isEditing && deliverieDetails) {
            form.setFieldsValue({
                orderId: deliverieDetails.orderId?._id || deliverieDetails.orderId,
                type: deliverieDetails.type || "Normal",
                status: deliverieDetails.status || "Pending",
                assignedDriverId: deliverieDetails.assignedDriverId?._id || deliverieDetails.assignedDriverId,
                estimatedDeliveryTime: deliverieDetails.estimatedDeliveryTime ? dayjs(deliverieDetails.estimatedDeliveryTime) : null,
                notes: deliverieDetails.notes || ""
            });
        } else {
            form.resetFields();
        }
    }, [isEditing, deliverieDetails, open, form]);

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();
            const payload = {
                ...values,
                estimatedDeliveryTime: values.estimatedDeliveryTime ? values.estimatedDeliveryTime.toISOString() : null
            };

            if (isEditing) {
                await DeliveryServ.update(deliverieDetails._id, payload);
                message.success("Delivery updated successfully");
            } else {
                await DeliveryServ.create(payload);
                message.success("Delivery created successfully");
            }
            refreshDeliveries(true);
        } catch (err) {
            console.error("Error saving delivery:", err);
            if (!err.errorFields) {
                message.error("Failed to save delivery");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={
                <h2 className="nz-modal-title">Manage Delivery</h2>
            }
            open={open}
            onCancel={() => refreshDeliveries(false)}
            className="custom-modal"
            footer={
                <div className="nz-form-footer">
                    <GlobalButton action="cancel" size="small" onClick={() => refreshDeliveries(false)} disabled={submitting}>
                        Cancel
                    </GlobalButton>
                    <GlobalButton action="submit" size="small" loading={submitting} onClick={handleSubmit}>
                        {isEditing ? "Update" : "Save"}
                    </GlobalButton>
                </div>
            }
            destroyOnClose
            centered
        >
            <Form form={form} layout="vertical" size="small" requiredMark={false}>
                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            name="orderId"
                            label={<span className="nz-form-label">Order ID</span>}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 6 }}
                        >
                            <Select placeholder="Choose an order" disabled loading={loading}>
                                {orders.map(order => (
                                    <Option key={order._id} value={order._id}>
                                        {order.orderId} - {order.totalAmount}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="assignedDriverId"
                            label={<span className="nz-form-label">Assign Driver</span>}
                            style={{ marginBottom: 6 }}
                        >
                            <Select placeholder="Select delivery staff member" allowClear loading={loading}>
                                {staff.map(member => (
                                    <Option key={member.userId?._id} value={member.userId?._id}>
                                        {member.userId?.firstName} {member.userId?.lastName} ({member.vehicleNumber})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label={<span className="nz-form-label">Delivery Type</span>}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 6 }}
                        >
                            <Select>
                                <Option value="Normal">Normal</Option>
                                <Option value="Express">Express</Option>
                                <Option value="Scheduled">Scheduled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="status"
                            label={<span className="nz-form-label">Status</span>}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 6 }}
                        >
                            <Select>
                                <Option value="Pending">Pending</Option>
                                <Option value="Assigned">Assigned</Option>
                                <Option value="Picked">Picked</Option>
                                <Option value="Out for Delivery">Out for Delivery</Option>
                                <Option value="Delivered">Delivered</Option>
                                <Option value="Cancelled">Cancelled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="estimatedDeliveryTime"
                            label={<span className="nz-form-label">Estimated Arrival</span>}
                            style={{ marginBottom: 6 }}
                        >
                            <DatePicker showTime className="pcu-w-100" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="notes"
                            label={<span className="nz-form-label">Notes</span>}
                            style={{ marginBottom: 8 }}
                        >
                            <Input.TextArea rows={3} placeholder="Additional notes..." />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default AddDeliveries;
